import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  GetMyLocationsV2LeanItem,
  useGetMyLocationsV2LeanQuery,
} from '../../services/locationServices/getMyLocationsV2';
import { mergePagedItems } from '../../utils/hostListingsPagination';
import { formatAddress } from '../../helpers/formatAddress';
import LoadingSpinner from '../LoadingSpinner';

const PAGE_SIZE = 20;

const Wrapper = styled.div`
  ${tw`w-full flex flex-col gap-3`}
`;

const SearchInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md text-sm`}
  box-sizing: border-box;
`;

const RadioList = styled.div`
  ${tw`w-full flex flex-col border border-border-default rounded-md overflow-hidden`}
`;

const RadioRow = styled.label<{ $selected?: boolean }>`
  ${tw`flex items-center gap-3 w-full px-3 py-3 text-sm cursor-pointer border-b border-border-default last:border-b-0`}
  ${({ $selected }) => ($selected ? tw`bg-bg-secondary` : tw`bg-white hover:bg-bg-secondary`)}
`;

const RadioInput = styled.input`
  ${tw`flex-shrink-0`}
`;

const RadioLabel = styled.span`
  ${tw`flex-1 text-left text-text-primary`}
`;

const CreateNewRow = styled.label`
  ${tw`flex items-center gap-3 w-full px-1 py-2 text-sm cursor-pointer`}
`;

const LoadMoreButton = styled.button`
  ${tw`text-sm underline text-text-secondary self-start mt-1`}
  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }
`;

const ErrorText = styled.span`
  ${tw`text-sm text-red-700`}
`;

const MetaText = styled.p`
  ${tw`text-xs text-text-secondary m-0`}
`;

export type LocationLoadMorePickerMeta = {
  totalCount: number;
  loading: boolean;
  error: boolean;
};

export type LocationLoadMorePickerProps = {
  value: number | null;
  onChange: (locationId: number | null, location?: GetMyLocationsV2LeanItem) => void;
  /** When true, show “Create new location” radio (Flutter SwitchListTile parity). */
  allowCreateNew?: boolean;
  useNewLocation?: boolean;
  onSelectCreateNew?: () => void;
  selectedLocation?: GetMyLocationsV2LeanItem | null;
  /** Parent empty-state / gates — avoids a second getMyLocationsV2 lean query. */
  onMetaChange?: (meta: LocationLoadMorePickerMeta) => void;
  dataTestId?: string;
};

function buildInput(page: number, q: string) {
  return {
    page,
    limit: PAGE_SIZE,
    ...(q ? { q } : {}),
  };
}

/**
 * Searchable host-location picker: radio list + Load more (Flutter add-car parity).
 * Spec 009 — search filters via `q`; no native &lt;select&gt;.
 *
 * Uses page-state + `network-only` (same as CarSwapModal / Earnings vehicle picker).
 * Avoids `cache-and-network` + `fetchMore`, which refetches page 1 in the background
 * and wipes merged load-more rows.
 */
export default function LocationLoadMorePicker({
  value,
  onChange,
  allowCreateNew = false,
  useNewLocation = false,
  onSelectCreateNew,
  selectedLocation: selectedLocationProp,
  onMetaChange,
  dataTestId = 'location-load-more-picker',
}: LocationLoadMorePickerProps) {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState<GetMyLocationsV2LeanItem[]>([]);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [selectedOption, setSelectedOption] = useState<GetMyLocationsV2LeanItem | null>(
    selectedLocationProp ?? null,
  );

  // Debounce search → q. Only reset paging when q actually changes
  // (the initial mount must not wipe items that just loaded).
  useEffect(() => {
    const nextQ = search.trim();
    const timer = window.setTimeout(() => {
      setQ(prev => {
        if (prev === nextQ) return prev;
        setPage(1);
        setLoaded([]);
        setLoadMoreError(false);
        return nextQ;
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (selectedLocationProp) {
      setSelectedOption(selectedLocationProp);
    }
  }, [selectedLocationProp]);

  const { data, loading, error, refetch } = useGetMyLocationsV2LeanQuery({
    variables: { input: buildInput(page, q) },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const paged = data?.getMyLocationsV2;
  const pageMatches = paged?.page === page;
  const totalCount = pageMatches ? (paged?.totalCount ?? 0) : 0;
  const hasMore = pageMatches && paged?.hasMore === true;
  const initialLoading = loading && loaded.length === 0;
  const loadingMore = loading && loaded.length > 0;

  useEffect(() => {
    if (!paged || paged.page !== page) return;
    setLoaded(prev => mergePagedItems(prev, paged.page, paged.items));
    setLoadMoreError(false);
  }, [data, page, paged]);

  useEffect(() => {
    if (error && page > 1) {
      setLoadMoreError(true);
    }
  }, [error, page]);

  const onMetaChangeRef = useRef(onMetaChange);
  onMetaChangeRef.current = onMetaChange;
  useEffect(() => {
    onMetaChangeRef.current?.({
      totalCount: pageMatches ? totalCount : loaded.length,
      loading: initialLoading,
      error: !!error && loaded.length === 0,
    });
  }, [totalCount, initialLoading, error, loaded.length, pageMatches]);

  const displayRows = (() => {
    if (
      selectedOption &&
      !useNewLocation &&
      value != null &&
      selectedOption.id === value &&
      !loaded.some(loc => loc.id === selectedOption.id)
    ) {
      return [selectedOption, ...loaded];
    }
    return loaded;
  })();

  const selectLocation = (location: GetMyLocationsV2LeanItem) => {
    setSelectedOption(location);
    onChange(location.id, location);
  };

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    setLoadMoreError(false);
    setPage(p => p + 1);
  };

  const handleRetryLoadMore = () => {
    setLoadMoreError(false);
    void refetch();
  };

  return (
    <Wrapper data-testid={dataTestId}>
      <SearchInput
        type="search"
        value={search}
        placeholder="Search locations"
        aria-label="Search locations"
        data-testid={`${dataTestId}-search`}
        onChange={e => setSearch(e.target.value)}
      />

      {allowCreateNew && (
        <CreateNewRow>
          <RadioInput
            type="radio"
            name={`${dataTestId}-choice`}
            checked={useNewLocation}
            onChange={() => onSelectCreateNew?.()}
            data-testid={`${dataTestId}-create-new`}
          />
          <RadioLabel>Create a new location</RadioLabel>
        </CreateNewRow>
      )}

      {!useNewLocation && (
        <>
          <MetaText>Existing locations</MetaText>
          {initialLoading ? (
            <LoadingSpinner containerClassName="min-h-[120px] w-full" />
          ) : (
            <>
              <RadioList role="radiogroup" aria-label="Choose a location">
                {displayRows.map(location => {
                  const selected = !useNewLocation && value === location.id;
                  const label = formatAddress(location, {
                    includeState: true,
                    includeZipCode: false,
                  });
                  return (
                    <RadioRow
                      key={location.id}
                      $selected={selected}
                      data-testid={`${dataTestId}-option-${location.id}`}
                    >
                      <RadioInput
                        type="radio"
                        name={`${dataTestId}-choice`}
                        checked={selected}
                        onChange={() => selectLocation(location)}
                        value={location.id}
                      />
                      <RadioLabel>
                        {location.name ? `${location.name} — ${label}` : label}
                      </RadioLabel>
                    </RadioRow>
                  );
                })}
              </RadioList>

              {(hasMore || loadMoreError) && (
                <LoadMoreButton
                  type="button"
                  disabled={loadingMore}
                  data-testid={`${dataTestId}-load-more`}
                  onClick={() => (loadMoreError ? handleRetryLoadMore() : handleLoadMore())}
                >
                  {loadingMore
                    ? 'Loading…'
                    : loadMoreError
                      ? 'Retry load more'
                      : 'Load more locations'}
                </LoadMoreButton>
              )}
            </>
          )}
        </>
      )}

      {totalCount === 0 && !initialLoading && !useNewLocation && pageMatches && (
        <ErrorText role="status">No locations match your search.</ErrorText>
      )}
      {error && loaded.length === 0 && (
        <ErrorText role="alert">
          Unable to load locations.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            Retry
          </button>
        </ErrorText>
      )}
    </Wrapper>
  );
}
